"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

/** iOS push: the same curve as the wishlist sheets. */
const PUSH = {
  type: "tween",
  duration: 0.35,
  ease: [0.32, 0.72, 0, 1],
} as const;

interface ISlideOverPanelProps {
  isOpen: boolean;
  title: string;
  subtitle?: string | null;
  onClose: () => void;
  /** Gets the scroll container, for infinite lists that watch it. */
  children: (scroller: HTMLDivElement | null) => React.ReactNode;
}

/**
 * The app's pushed screen on a phone: a full-screen panel sliding in from the right over
 * the page, which stays where it was underneath. Mobile only (`md:hidden`).
 */
const SlideOverPanel: React.FC<ISlideOverPanelProps> = ({
  isOpen,
  title,
  subtitle,
  onClose,
  children,
}) => {
  const [mounted, setMounted] = useState(false);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  useEffect(() => setMounted(true), []);

  useLockBodyScroll(isOpen);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key={title}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={PUSH}
          className="fixed inset-0 z-[1000000] flex flex-col bg-white shadow-[-12px_0_24px_-12px_rgba(0,0,0,0.18)] md:hidden"
        >
          <header className="flex h-14 shrink-0 items-center gap-1 border-b border-[#EBEBEB] px-2 pt-[env(safe-area-inset-top)]">
            <button
              type="button"
              onClick={onClose}
              aria-label="Back"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink active:bg-gray-100"
            >
              <ChevronLeft size={24} />
            </button>
            <h2 className="min-w-0 flex-1 truncate pr-10 text-center text-base font-semibold text-ink">
              {title}
            </h2>
          </header>
          <div
            ref={setScroller}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-10 pt-4"
          >
            {subtitle && (
              <p className="mb-5 text-sm text-ink-secondary">{subtitle}</p>
            )}
            {children(scroller)}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default SlideOverPanel;
